import { PropertyOwnershipVerificationState } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_getVerificationState } from '@zg-rentals/ts-rental-manager-api-client-react-query';

// The states the server lets a listing publish in (ZRM-16878).
const VERIFIED_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.COMPLETED,
  PropertyOwnershipVerificationState.APPROVED,
];

export const isOwnershipVerified = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && VERIFIED_STATES.includes(state));

// Everything else needs the step: publish is gated on the verified states, so hiding it in a blocking
// state dead-ends the landlord. The test documents the full mapping.
const STATES_WITHOUT_VERIFICATION_WORK: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.NOT_APPLICABLE,
  ...VERIFIED_STATES,
];

/** An unrecognised state reads as "show" — likelier to be blocking than resolved. */
export const shouldShowStepForState = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && !STATES_WITHOUT_VERIFICATION_WORK.includes(state));

/**
 * States `startOrResume` can act on. Narrower than `shouldShowStepForState`: the failure states still
 * show the step, but their route forward is manual review, not another inquiry.
 */
const STARTABLE_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.NOT_STARTED,
  PropertyOwnershipVerificationState.CREATED,
  PropertyOwnershipVerificationState.PENDING,
  PropertyOwnershipVerificationState.EXPIRED,
];

export const isStartableState = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && STARTABLE_STATES.includes(state));

export const isAwaitingManualReview = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  state === PropertyOwnershipVerificationState.NEEDS_REVIEW;

/**
 * Verification came back negative. `FAILED` is documented as appealable and `DECLINED` as final, but
 * there is no endpoint to request an appeal, so both read the same to the landlord for now (ZRM-17096).
 */
const FAILED_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.FAILED,
  PropertyOwnershipVerificationState.DECLINED,
];

export const hasVerificationFailed = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && FAILED_STATES.includes(state));

/** Anything that can change the server's applicability answer must invalidate this. */
export const OWNERSHIP_VERIFICATION_STATE_TAG = 'ownershipVerificationState';

/**
 * What the server says about a listing's ownership verification. Callers own both gates: the rollout
 * flag, and holding the lookup until the listing has a `forRentBy` — applicability is derived from it,
 * so asking earlier caches a `NOT_APPLICABLE` for a listing that qualifies a few steps later.
 */
export const useOwnershipVerificationState = ({
  listingAlias,
  enabled = true,
}: {
  listingAlias: string;
  enabled?: boolean;
}) => {
  const { data, isLoading, isError, refetch } =
    useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_getVerificationState(
      { listingAlias },
      {
        enabled: enabled && Boolean(listingAlias),
        meta: {
          tags: [OWNERSHIP_VERIFICATION_STATE_TAG, listingAlias],
          context: {
            api: 'getVerificationState',
            details: { listingAlias },
          },
        },
      },
    );

  const state = data?.state;

  return {
    state,
    shouldShowVerificationStep: shouldShowStepForState(state),
    isVerified: isOwnershipVerified(state),
    isStartable: isStartableState(state),
    // Every flag above reads false before the lookup lands, indistinguishably from a real answer.
    isStateLoading: isLoading,
    // react-query keeps the last successful data when a refetch fails, so `isError` alone would
    // discard an outcome we already know.
    isStateError: isError && !state,
    refetchState: refetch,
  };
};
